import React, {
  createContext,
  useState,
  useContext,
} from "react";

export const DEFAULT_PDF_COLORS = {
  primary: "#1E293B",
  secondary: "#2563EB",
  headerText: "#FFFFFF",
  bodyText: "#0F172A",
  tableHeader: "#1E293B",
  tableHeaderText: "#FFFFFF",
  alternateRow: "#F8FAFC",
  border: "#E2E8F0",
  accent: "#FFFFFF",
  mutedText: "#64748B",
};

const PdfSettingsContext = createContext();

/* =========================================================
   PDF SETTINGS PROVIDER
========================================================= */

export const PdfSettingsProvider = ({ children }) => {
  /* =========================================================
     PDF BACKGROUND COLOR

     IMPORTANT:
     This is completely independent from the global
     application/sidebar primaryColor.
  ========================================================= */

  const [pdfThemeColor, setPdfThemeColor] = useState(() => {
    if (typeof window === "undefined") {
      return DEFAULT_PDF_COLORS.primary;
    }

    return (
      localStorage.getItem("pdfThemeColor") ||
      DEFAULT_PDF_COLORS.primary
    );
  });

  /* =========================================================
     PDF TEXT COLOR

     Completely independent global PDF setting.
  ========================================================= */

  const [pdfTextColor, setPdfTextColor] = useState(() => {
    if (typeof window === "undefined") {
      return DEFAULT_PDF_COLORS.bodyText;
    }

    return (
      localStorage.getItem("pdfTextColor") ||
      DEFAULT_PDF_COLORS.bodyText
    );
  });

  /* =========================================================
     ADMISSION PREFIX
  ========================================================= */

  const [admissionPrefix, setAdmissionPrefix] = useState(() => {
    if (typeof window === "undefined") {
      return "ADM";
    }

    return (
      localStorage.getItem("admissionPrefix") ||
      "ADM"
    );
  });

  /* =========================================================
     UPDATE PDF BACKGROUND COLOR

     This updates ONLY:
     - pdfThemeColor state
     - pdfThemeColor localStorage

     It does NOT touch primaryColor.
  ========================================================= */

  const updatePdfThemeColor = (color) => {
    if (!color) {
      return;
    }

    const normalizedColor = String(color).trim();

    if (!normalizedColor) {
      return;
    }

    setPdfThemeColor(normalizedColor);

    if (typeof window !== "undefined") {
      localStorage.setItem(
        "pdfThemeColor",
        normalizedColor
      );
    }
  };

  /* =========================================================
     UPDATE PDF TEXT COLOR

     This updates ONLY:
     - pdfTextColor state
     - pdfTextColor localStorage
  ========================================================= */

  const updatePdfTextColor = (color) => {
    if (!color) {
      return;
    }

    const normalizedColor = String(color).trim();

    if (!normalizedColor) {
      return;
    }

    setPdfTextColor(normalizedColor);

    if (typeof window !== "undefined") {
      localStorage.setItem(
        "pdfTextColor",
        normalizedColor
      );
    }
  };

  /* =========================================================
     UPDATE ADMISSION PREFIX
  ========================================================= */

  const updateAdmissionPrefix = (prefix) => {
    if (!prefix) {
      return;
    }

    const normalizedPrefix = String(prefix).trim();

    if (!normalizedPrefix) {
      return;
    }

    setAdmissionPrefix(normalizedPrefix);

    if (typeof window !== "undefined") {
      localStorage.setItem(
        "admissionPrefix",
        normalizedPrefix
      );
    }
  };

  /* =========================================================
     CONTEXT
  ========================================================= */

  return (
    <PdfSettingsContext.Provider
      value={{
        pdfThemeColor,
        pdfTextColor,
        admissionPrefix,

        updatePdfThemeColor,
        updatePdfTextColor,
        updateAdmissionPrefix,
      }}
    >
      {children}
    </PdfSettingsContext.Provider>
  );
};

/* =========================================================
   HOOK
========================================================= */

export const usePdfSettings = () =>
  useContext(PdfSettingsContext);